/**
 * Register Page — Industry design system ("Criar Conta" artboard grammar).
 *
 * Centered Z plate + wordmark over a blueprint card with uppercase title,
 * context line (invite / activation), a 2-column grid of grammar fields
 * (uppercase mini-labels over square hairline inputs), ink-bordered error
 * box and a dashed-top footer row with the primary CTA.
 *
 * Supports invite token flow for post-checkout and team invites — all
 * validation / claim-invite / session-install logic is unchanged.
 */

import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, CircularProgress, Link } from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import oauthService from '../services/oauth';
import authService from '../services/auth';
import { resolvePostLoginPath } from '../services/postLoginDestination';

const ROLE_LABELS = {
  admin: 'Administrador',
  pilot: 'Piloto',
  mechanic: 'Mecânico',
  finance: 'Financeiro',
  analyst: 'Analista',
  secretary: 'Secretária',
};

const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED68 = 'color-mix(in srgb, var(--color-text) 68%, transparent)';
const MUTED72 = 'color-mix(in srgb, var(--color-text) 72%, transparent)';

const labelSx = {
  display: 'block',
  fontFamily: HEADING,
  fontSize: '9.5px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: MUTED58,
  mb: '4px',
};

const inputStyle = {
  width: '100%',
  minHeight: 38,
  fontSize: 14,
  background: 'transparent',
  border: '1px solid var(--color-divider)',
  borderRadius: 0,
  padding: '0 11px',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
};

const hintSx = {
  fontFamily: HEADING,
  fontSize: 9,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'color-mix(in srgb, var(--color-text) 52%, transparent)',
  mt: '4px',
};

const primaryBtnSx = {
  minHeight: 40,
  px: '18px',
  border: `1px solid ${ACC}`,
  background: ACC,
  color: 'var(--color-bg)',
  fontFamily: HEADING,
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: 0,
  cursor: 'pointer',
  '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
  '&:active': { background: ACC7 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

const eyeSx = {
  position: 'absolute',
  right: 4,
  top: '50%',
  transform: 'translateY(-50%)',
  color: MUTED58,
  borderRadius: 0,
};

const Register = ({ mode = 'register' }) => {
  const navigate = useNavigate();
  const { register, installSession } = useAuth();
  const [searchParams] = useSearchParams();

  // Invite token from URL
  const inviteToken = searchParams.get('token');

  // State
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(!!inviteToken);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    passwordConfirm: '',
    fullName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isManualSetupInvite = inviteInfo?.invite_type === 'manual_setup_link';
  const isCheckoutAdminActivation = inviteInfo?.invite_source === 'checkout' && inviteInfo?.role === 'admin';
  const isActivationMode = mode === 'activation' || isManualSetupInvite;
  const activationMissingToken = mode === 'activation' && !inviteToken;
  const pageTitle = isCheckoutAdminActivation
    ? 'Ativar administrador da empresa'
    : isActivationMode
      ? 'Ativar acesso'
    : inviteInfo
      ? 'Concluir cadastro'
      : 'Criar conta';
  const submitLabel = loading
    ? (isActivationMode ? 'Ativando acesso...' : 'Criando conta...')
    : (isActivationMode ? 'Ativar acesso' : 'Criar conta');

  const validateInviteToken = useCallback(async () => {
    setLoadingInvite(true);
    try {
      const result = await oauthService.validateInvite(inviteToken);
      if (result.valid) {
        setInviteInfo(result);
        setFormData(prev => ({
          ...prev,
          email: result.email,
          fullName: prev.fullName || result.full_name || '',
        }));
      } else {
        setError(result.error || 'Link de convite inválido ou expirado');
      }
    } catch (err) {
      setError('Não foi possível validar o link de convite');
    } finally {
      setLoadingInvite(false);
    }
  }, [inviteToken]);

  // Validate invite token on mount
  useEffect(() => {
    if (inviteToken) {
      authService.logout();
      validateInviteToken();
    }
  }, [inviteToken, validateInviteToken]);

  useEffect(() => {
    if (activationMissingToken) {
      setError('Link de ativação inválido ou ausente.');
    }
  }, [activationMissingToken]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activationMissingToken) {
      setError('Link de ativação inválido ou ausente.');
      return;
    }
    setError('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.passwordConfirm) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      setLoading(false);
      return;
    }

    try {
      if (inviteToken) {
        authService.logout();
        // Claim invite with password. The backend creates the user inside
        // the freshly-provisioned tenant schema and returns a JWT pair
        // whose `tenant_schema` claim already points at that tenant.
        const claimResult = await oauthService.claimInviteWithPassword(inviteToken, {
          username: formData.username,
          password: formData.password,
          passwordConfirm: formData.passwordConfirm,
          fullName: formData.fullName,
        });

        const access = claimResult?.access;
        const refresh = claimResult?.refresh;
        const tenantSchema = (
          claimResult?.tenant_schema
          || claimResult?.user?.tenant_schema
          || inviteInfo?.tenant_schema
          || ''
        ).trim();

        if (access && refresh) {
          // Install the JWT pair directly so the user lands on the
          // dashboard scoped to their *own* tenant. We deliberately do
          // NOT round-trip through /auth/login/: dropping these tokens
          // and relying on a URL-encoded `tenant_schema` hint was the
          // source of the 2026-05 cross-tenant data-leak regression.
          try {
            await installSession({ access, refresh, user: claimResult?.user });
            // A freshly created account lands on Primeiros Passos; a member
            // joining an existing operation (data already present) lands on
            // Mission Control. The resolver reads onboarding status and falls
            // back to Mission Control on any error.
            navigate(await resolvePostLoginPath(), { replace: true });
            return;
          } catch (installErr) {
            console.warn(
              '[Register] Direct session install failed; attempting credential-based login fallback.',
              installErr,
            );
          }
        }

        // Resilience step: the backend just persisted the user's
        // password during claim-invite. Try a programmatic login with
        // those exact credentials BEFORE dropping the user on the
        // /login page. This rescues the common transient failure modes
        // (e.g. a flaky network during installSession's profile fetch,
        // a brief schema-routing hiccup, or the tokens-missing-from-
        // response edge case) without forcing the user to type the
        // password they JUST typed all over again. If this also fails,
        // we fall through to the manual /login redirect with a clear
        // recovery CTA on the Login page itself.
        try {
          await authService.login(
            formData.username,
            formData.password,
            tenantSchema,
          );
          navigate(await resolvePostLoginPath(), { replace: true });
          return;
        } catch (autoLoginErr) {
          console.warn(
            '[Register] Auto-login fallback after claim-invite failed; sending user to /login.',
            autoLoginErr,
          );
        }

        // Final fallback: send the user to /login with everything we
        // know so the Login page can pre-fill the form, surface the
        // "account just created" banner, and (on a 401) lead with a
        // one-tap "Recuperar minha senha" CTA pointed at the right
        // email.
        const nextEmail = inviteInfo?.email || formData.email || '';
        const nextUsername = formData.username || nextEmail;
        const loginParams = new URLSearchParams({
          account_created: '1',
        });
        if (nextEmail) loginParams.set('email', nextEmail);
        if (nextUsername) loginParams.set('username', nextUsername);
        if (tenantSchema) loginParams.set('tenant_schema', tenantSchema);
        loginParams.set('access_hint', 'password');
        navigate(`/login?${loginParams.toString()}`);
      } else {
        // Regular registration
        await register(formData);
        navigate('/login');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Não foi possível concluir o cadastro';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvite) {
    return (
      <Box
        className="zb-industry"
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-bg)',
        }}
      >
        <CircularProgress sx={{ color: ACC }} />
      </Box>
    );
  }


  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: '34px 24px 48px',
        boxSizing: 'border-box',
      }}
    >
      {/* Z plate + wordmark */}
      <Box
        component="a"
        href="/login"
        sx={{ display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none', color: 'var(--color-text)' }}
      >
        <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1.5px solid var(--color-text)', fontFamily: HEADING, fontSize: 15, fontWeight: 600 }}>
          Z
        </Box>
        <Box component="span" sx={{ fontFamily: HEADING, fontSize: 19, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Zulbot
        </Box>
      </Box>

      <Box sx={{ width: '100%', maxWidth: 620, mt: '26px' }}>
        <Box
          className="blueprint"
          sx={{ p: '24px 24px 20px', background: 'transparent', boxShadow: 'var(--ind-shadow-md)' }}
        >
          <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

          <Typography
            component="h1"
            sx={{ m: 0, fontFamily: HEADING, fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase' }}
          >
            {pageTitle}
          </Typography>

          {inviteInfo && !isActivationMode && (
            <Typography sx={{ m: '6px 0 0', fontSize: 13.5, lineHeight: 1.55, color: MUTED72 }}>
              Você foi convidado(a) para participar de <strong>{inviteInfo.company_name}</strong> como <strong>{ROLE_LABELS[inviteInfo.role] || inviteInfo.role}</strong>.
            </Typography>
          )}
          {inviteInfo && isActivationMode && (
            <Typography sx={{ m: '6px 0 0', fontSize: 13.5, lineHeight: 1.55, color: MUTED72 }}>
              {isCheckoutAdminActivation ? (
                <>
                  Você está criando a conta administradora da empresa <strong>{inviteInfo.company_name}</strong>.
                  Depois disso, novos usuários devem ser adicionados na página Admin • Usuários e Acessos.
                </>
              ) : (
                <>
                  Você está sendo adicionado(a) como <strong>{(ROLE_LABELS[inviteInfo.role] || inviteInfo.role)}</strong> na equipe de <strong>{inviteInfo.company_name}</strong>.
                  Defina o usuário e a senha que você vai usar no primeiro login.
                </>
              )}
            </Typography>
          )}
          {!inviteInfo && (
            <Typography sx={{ m: '6px 0 0', fontSize: 13.5, lineHeight: 1.55, color: MUTED72 }}>
              {isActivationMode
                ? 'Use o link recebido para concluir a ativação da conta.'
                : 'Crie seu acesso para gerenciar a operação de aviação no Zulbot.'}
            </Typography>
          )}

          {/* Error box — ink border, no severity coloring */}
          {error && (
            <Box sx={{ mt: '14px', p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.45, color: INK }}>
              {error}
            </Box>
          )}

          {/* Registration form */}
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: '14px 18px',
                mt: '20px',
              }}
            >
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Box component="label" htmlFor="register-full-name" sx={labelSx}>
                  Nome completo
                </Box>
                <input
                  id="register-full-name"
                  className="ind-input"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  autoComplete="name"
                  style={inputStyle}
                />
              </Box>

              <Box sx={{ gridColumn: isActivationMode ? '1 / -1' : 'auto' }}>
                <Box component="label" htmlFor="register-username" sx={labelSx}>
                  Usuário
                </Box>
                <input
                  id="register-username"
                  className="ind-input"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  autoComplete="username"
                  style={inputStyle}
                />
              </Box>

              {!isActivationMode && (
                <Box>
                  <Box component="label" htmlFor="register-email" sx={labelSx}>
                    E-mail
                  </Box>
                  <input
                    id="register-email"
                    className="ind-input"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={!!inviteInfo}
                    autoComplete="email"
                    style={{ ...inputStyle, opacity: inviteInfo ? 0.6 : 1 }}
                  />
                  {inviteInfo ? <Box sx={hintSx}>E-mail definido pelo convite</Box> : null}
                </Box>
              )}

              <Box>
                <Box component="label" htmlFor="register-password" sx={labelSx}>
                  Senha
                </Box>
                <Box sx={{ position: 'relative' }}>
                  <input
                    id="register-password"
                    className="ind-input"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                    style={{ ...inputStyle, paddingRight: 44 }}
                  />
                  <IconButton
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    size="small"
                    sx={eyeSx}
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Box>
                <Box sx={hintSx}>
                  Mínimo de 8 caracteres
                </Box>
              </Box>

              <Box>
                <Box component="label" htmlFor="register-password-confirm" sx={labelSx}>
                  Confirmar senha
                </Box>
                <Box sx={{ position: 'relative' }}>
                  <input
                    id="register-password-confirm"
                    className="ind-input"
                    name="passwordConfirm"
                    type={showPasswordConfirm ? 'text' : 'password'}
                    value={formData.passwordConfirm}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                    style={{ ...inputStyle, paddingRight: 44 }}
                  />
                  <IconButton
                    onClick={() => setShowPasswordConfirm((s) => !s)}
                    aria-label={showPasswordConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                    size="small"
                    sx={eyeSx}
                  >
                    {showPasswordConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                mt: '20px',
                pt: 2,
                borderTop: '1px dashed var(--color-neutral-300)',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1 }} />
              <Box
                component="button"
                type="submit"
                disabled={loading || activationMissingToken}
                sx={primaryBtnSx}
              >
                {submitLabel}
              </Box>
            </Box>
          </form>
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: 2, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ fontSize: 12.5, color: MUTED68 }}>
            Já possui usuário e senha?{' '}
          </Box>
          <Link
            href="/login"
            underline="none"
            sx={{
              fontFamily: HEADING,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: ACC7,
              '&:hover': { color: ACC8 },
            }}
          >
            Entrar
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
